import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';
import { experienceBySlug, primaryImage } from '../data/experiences';
import { trackViewExperience, trackWhatsAppClick } from '../lib/analytics';
import { experienceFaqs } from '../data/faq';
import { whatsappLink } from '../data/site';
import { PackageGroupCard } from '../components/experience/PackageGroupCard';
import { Faq } from '../components/Faq';
import { NotFoundPage } from './NotFoundPage';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { imgDims } from '../data/imageMeta';
import { useBooking } from '../components/booking';
import { useExperiences, useExperiencePricing } from '../hooks';

export function ExperienceDetailPage({ slug }: { slug: string }) {
  const exp = experienceBySlug(slug);
  const { openBooking } = useBooking();
  const { byAlias } = useExperiences();
  const dyn = byAlias(exp?.productAlias ?? '');
  const { livePrice, status: pricingStatus } = useExperiencePricing(dyn?.id);
  useDocumentTitle(exp ? `${exp.pageTitle} | Flying Muse` : 'Flying Muse');

  // GA4 `view_item` once per viewed experience.
  useEffect(() => {
    if (exp) trackViewExperience(exp);
  }, [exp]);

  if (!exp) return <NotFoundPage />;

  // Live "from" price only — no static fallback.
  const fromPrice = dyn?.startingDiscountedPrice ?? dyn?.startingPrice ?? null;

  const heroImage = primaryImage(exp);
  const { width: imgW, height: imgH } = imgDims(heroImage);
  const galleryImages = exp.images.slice(1);
  const faqs = experienceFaqs[exp.id] ?? [];
  const chatHref = whatsappLink(
    `Hi Flying Muse! I'm interested in the ${exp.pageTitle}. Could you share availability and the next steps?`,
  );

  return (
    <article>
      {/* ---------------------------------------------------------------- */}
      {/* Hero                                                              */}
      {/* ---------------------------------------------------------------- */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="" aria-hidden="true" width={imgW} height={imgH} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/90 via-navy-deep/65 to-navy-deep/35" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/80 via-transparent to-navy-deep/40" />
        </div>

        <div className="container-content section-pad relative z-10 pb-16 pt-32 sm:pb-20 sm:pt-40">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 font-sans text-[0.62rem] font-medium uppercase tracking-wide2 text-white/55">
            <Link to="/experiences" className="transition-colors hover:text-white">
              Experiences
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white/80">{exp.kicker}</span>
          </nav>

          <p className="eyebrow mt-7 text-crimson-soft">{exp.kicker}</p>
          <h1 className="mt-4 max-w-3xl font-display text-[clamp(2.5rem,6vw,4.25rem)] font-medium leading-[1.05] text-white">
            {exp.pageTitle}
          </h1>
          <p className="mt-6 max-w-2xl font-sans text-base font-light leading-relaxed text-white/85">
            {exp.intro}
          </p>

          {/* Audience + from price */}
          <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-4">
            <ul className="flex items-center gap-6">
              {exp.audience.map(({ label, Icon }) => (
                <li key={label} className="flex items-center gap-2 text-white/85">
                  <Icon className="h-5 w-5 text-crimson-soft" strokeWidth={1.6} />
                  <span className="font-sans text-[0.7rem] font-medium uppercase tracking-wide2">
                    {label}
                  </span>
                </li>
              ))}
            </ul>
            {fromPrice != null && (
              <span className="font-sans text-[0.7rem] font-medium uppercase tracking-wide2 text-white/55">
                From{' '}
                <span className="font-display text-xl normal-case tracking-normal text-white">
                  &euro;{fromPrice}
                </span>
              </span>
            )}
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button type="button" onClick={() => openBooking(exp, undefined, 'experience_detail_hero')} className="btn btn-primary">
              Book Now
            </button>
            <a href="#packages" className="btn btn-outline-light">
              View Packages &amp; Prices
            </a>
          </div>
          <p className="mt-4 font-sans text-xs font-light text-white/70">
            Prefer to chat first?{' '}
            <a
              href={chatHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('experience_detail_hero', exp)}
              className="font-medium text-white underline-offset-2 hover:underline"
            >
              Message us on WhatsApp
            </a>
          </p>
        </div>
      </header>

      {/* ---------------------------------------------------------------- */}
      {/* Intro + What's included                                          */}
      {/* ---------------------------------------------------------------- */}
      <section className="bg-cream py-20 sm:py-24">
        <div className="container-content section-pad grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
          <div>
            <p className="eyebrow">The Experience</p>
            <h2 className="mt-4 font-display text-[clamp(1.9rem,4vw,2.75rem)] font-medium text-charcoal">
              What makes it special
            </h2>
            <p className="mt-6 font-sans text-[0.98rem] font-light leading-relaxed text-stone">
              {exp.intro}
            </p>

            {exp.idealFor && (
              <div className="mt-8">
                <p className="font-sans text-[0.62rem] font-semibold uppercase tracking-label text-charcoal/55">
                  Ideal for
                </p>
                <ul className="mt-4 flex flex-wrap gap-2.5">
                  {exp.idealFor.map((item) => (
                    <li
                      key={item}
                      className="rounded-full border border-charcoal/15 bg-white px-4 py-1.5 font-sans text-xs font-medium text-charcoal/75"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-white p-8 shadow-card sm:p-10">
            <p className="font-sans text-[0.62rem] font-semibold uppercase tracking-label text-crimson">
              Included
            </p>
            <ul className="mt-6 grid gap-x-8 gap-y-3.5 sm:grid-cols-2">
              {exp.included.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-crimson" strokeWidth={2.4} />
                  <span className="font-sans text-sm font-light leading-snug text-charcoal/80">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Gallery                                                           */}
      {/* ---------------------------------------------------------------- */}
      {galleryImages.length > 0 && (
        <section className="bg-cream pb-4 sm:pb-8">
          <div className="container-content section-pad">
            <div
              className={`grid gap-4 sm:gap-5 ${
                galleryImages.length === 1 ? 'sm:grid-cols-1' : 'sm:grid-cols-2'
              }`}
            >
              {galleryImages.map((src) => {
                const { width, height } = imgDims(src);
                return (
                  <div
                    key={src}
                    className="overflow-hidden rounded-2xl shadow-card"
                  >
                    <img
                      src={src}
                      alt={`${exp.pageTitle} photoshoot`}
                      loading="lazy"
                      width={width}
                      height={height}
                      className="aspect-[4/3] h-full w-full object-cover transition-transform duration-[1.2s] ease-out hover:scale-105"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Packages & pricing                                               */}
      {/* ---------------------------------------------------------------- */}
      <section id="packages" className="scroll-mt-24 bg-cream-soft py-20 sm:py-24">
        <div className="container-content section-pad">
          <div className="text-center">
            <p className="eyebrow eyebrow--center">Packages &amp; Pricing</p>
            <h2 className="mt-4 font-display text-[clamp(2rem,4.5vw,3rem)] font-medium text-charcoal">
              Choose your session
            </h2>
          </div>

          <div className="mx-auto mt-12 flex max-w-4xl flex-col gap-7">
            {exp.packages.map((group) => (
              <PackageGroupCard
                key={group.id}
                group={group}
                experience={exp}
                livePrice={livePrice}
                pricingStatus={pricingStatus}
              />
            ))}
          </div>

          {/* Delivery note */}
          <div className="mx-auto mt-8 max-w-4xl text-center">
            {exp.deliveryNote.map((line) => (
              <p key={line} className="font-sans text-xs font-light text-stone">
                {line}
              </p>
            ))}
          </div>

          {/* Add-ons */}
          {exp.addOns && exp.addOns.length > 0 && (
            <div className="mx-auto mt-10 max-w-4xl rounded-2xl border border-charcoal/10 bg-cream px-6 py-7 sm:px-8">
              <p className="font-sans text-[0.62rem] font-semibold uppercase tracking-label text-crimson">
                Optional add-ons
              </p>
              <ul className="mt-4 flex flex-wrap gap-2.5">
                {exp.addOns.map((item) => (
                  <li
                    key={item}
                    className="rounded-full border border-charcoal/15 bg-white px-4 py-1.5 font-sans text-xs font-medium text-charcoal/75"
                  >
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-4 font-sans text-xs font-light italic text-stone">
                Add-ons are arranged on request - just mention them when you book.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* FAQ                                                               */}
      {/* ---------------------------------------------------------------- */}
      {faqs.length > 0 && (
        <section className="bg-cream py-20 sm:py-24">
          <div className="container-content section-pad">
            <div className="text-center">
              <p className="eyebrow eyebrow--center">Good to Know</p>
              <h2 className="mt-4 font-display text-[clamp(2rem,4.5vw,3rem)] font-medium text-charcoal">
                Frequently asked questions
              </h2>
            </div>
            <div className="mt-12">
              <Faq items={faqs} />
            </div>
          </div>
        </section>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Closing CTA                                                       */}
      {/* ---------------------------------------------------------------- */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="" aria-hidden="true" width={imgW} height={imgH} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/90 via-navy-deep/55 to-transparent" />
          <div className="absolute inset-0 bg-navy-deep/15" />
        </div>
        <div className="container-content section-pad relative z-10 py-20 sm:py-28">
          <div className="max-w-xl">
            <p className="eyebrow text-crimson-soft">Ready When You Are</p>
            <h2 className="mt-5 font-display text-[clamp(2rem,5vw,3.5rem)] font-medium leading-[1.06] text-white">
              Let&rsquo;s plan your {exp.kicker.toLowerCase()} session.
            </h2>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button type="button" onClick={() => openBooking(exp, undefined, 'experience_detail_footer')} className="btn btn-primary">
                Book Now
              </button>
              <a
                href={chatHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick('experience_detail_footer', exp)}
                className="btn btn-outline-light"
              >
                Ask on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
